import React from "react";
import { Link } from "react-router-dom";
import { ARTICLES } from "../data/articles";
import useSeo from "../hooks/useSeo";
import NewsletterCTA from "../components/NewsletterCTA";
import { Calendar, Clock, ArrowRight, BookOpen } from "lucide-react";

export default function BlogIndexPage() {
  useSeo({
    title: "Blog — Kurer-guides, priser og tips · EMA Kurer ApS",
    description:
      "Læs vores guides om kurérservice, priser, leveringstider og B2B-aftaler. Få indsigt der hjælper dig med at vælge den rigtige kurer.",
    path: "/blog",
  });

  // Build BlogPosting JSON-LD for the index
  const blogJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "EMA Kurer Blog",
    "url": "https://emakurer.dk/blog",
    "publisher": {
      "@type": "Organization",
      "name": "EMA Kurer ApS",
      "logo": { "@type": "ImageObject", "url": "https://emakurer.dk/og-default.jpg" },
    },
    "blogPost": ARTICLES.map((a) => ({
      "@type": "BlogPosting",
      "headline": a.title,
      "description": a.excerpt,
      "url": `https://emakurer.dk/blog/${a.slug}`,
      "datePublished": a.date,
      "author": { "@type": "Organization", "name": "EMA Kurer ApS" },
    })),
  };

  return (
    <div className="bg-brand-bg min-h-screen" data-testid="blog-index-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogJsonLd) }} />

      {/* Hero */}
      <section className="bg-brand-dark text-white py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-5">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-brand-red/20 text-brand-red px-3 py-1.5 rounded-full mb-4">
            <BookOpen size={14} /> Blog
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Kurer-guides, priser og praktiske tips
          </h1>
          <p className="mt-4 text-lg text-white/80 max-w-2xl">
            Få overblik over kurérservice i Danmark — fra prismodeller og B2B-aftaler til pakketips og leveringstider.
          </p>
        </div>
      </section>

      {/* Articles grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ARTICLES.map((a) => (
              <article
                key={a.slug}
                className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`article-card-${a.slug}`}
              >
                <Link to={`/blog/${a.slug}`} className="block p-6">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-brand-text2 mb-3">
                    <span className="font-bold text-brand-red">{a.category}</span>
                    <span className="flex items-center gap-1"><Calendar size={12} />{a.date}</span>
                    <span className="flex items-center gap-1"><Clock size={12} />{a.readMinutes} min</span>
                  </div>
                  <h2 className="font-display text-xl sm:text-2xl font-bold text-brand-dark group-hover:text-brand-red transition-colors leading-snug">
                    {a.title}
                  </h2>
                  <p className="mt-3 text-brand-text2 text-sm leading-relaxed">{a.excerpt}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-brand-red font-semibold text-sm">
                    Læs artiklen <ArrowRight size={14} />
                  </div>
                </Link>
              </article>
            ))}
          </div>

          <div className="mt-12">
            <NewsletterCTA source="blog_index" variant="inline" />
          </div>
        </div>
      </section>
    </div>
  );
}
